/**
 * The set of fields a record's create modal asks for — the same set the
 * record overview keeps visible as click-to-fill placeholders when they are
 * empty, so a record reads the same way whether it was just created or is
 * being reviewed a year later (a stable row set is what makes the panel
 * scannable: Company is always the fourth row, filled or not).
 *
 * One source of truth per entity type:
 *  - Registry-configured types (contact, company, deal, …) declare
 *    `createConfig.createProperties` / `createAssociations`.
 *  - Custom (`org_*`) types have no static config; their create modal is
 *    derived from the live schema by `GenericCrmList` (every user-defined,
 *    writable property, plus the stage select for pipelined types and the
 *    domain associations `CrmCreateForm` keeps after its kind filter). This
 *    mirrors that derivation.
 */

import type { AssociationType } from '../../core/SchemaClient';
import type { PropertySchemaMap } from '../../hooks/usePropertySchema';
import { counterpartEntityKinds } from '../../utils/associationMatching';
import {
  CREATE_FORM_DOMAIN_KINDS,
  deriveCreateAssociationNames,
} from '../../utils/genericCreateConfig';
import { hasPropertyEditor } from '../../utils/propertyEditability';
import type { EntityTypeConfig } from '../registry/entityTypeRegistry';

export interface CreateFormFieldKeys {
  properties: ReadonlySet<string>;
  associations: ReadonlySet<string>;
}

const EMPTY_KEYS: ReadonlySet<string> = new Set();

export const NO_CREATE_FORM_FIELD_KEYS: CreateFormFieldKeys = {
  properties: EMPTY_KEYS,
  associations: EMPTY_KEYS,
};

export interface GetCreateFormFieldKeysOptions {
  config: Pick<EntityTypeConfig, 'createConfig'>;
  entityTypeName: string;
  schema: PropertySchemaMap;
  associationTypes: readonly AssociationType[];
  /** entity_type_name -> entity_kind[] (from buildEntityKindMap). */
  entityKindMap: ReadonlyMap<string, readonly string[]>;
  /** Stage entity-type names for this org (from usePipelineStages). */
  stageTypeNames: ReadonlySet<string>;
}

export function getCreateFormFieldKeys({
  config,
  entityTypeName,
  schema,
  associationTypes,
  entityKindMap,
  stageTypeNames,
}: GetCreateFormFieldKeysOptions): CreateFormFieldKeys {
  if (config.createConfig) {
    return {
      properties: new Set(config.createConfig.createProperties),
      associations: new Set(config.createConfig.createAssociations),
    };
  }

  // Custom types — mirror GenericCrmList's schema-driven derivation. A
  // property with an `entity_id` was created as an ahoy_property_type event
  // (user-defined `org_*` properties and kind-stamped overrides alike); pure
  // kind-stamped templates have none and are left out of the create form so
  // they don't clutter it. The editor gate mirrors deriveCreatePropertyNames:
  // a field the create form can't render an input for isn't in the form.
  const properties = new Set<string>();
  for (const propSchema of Object.values(schema)) {
    if (
      propSchema.entity_id != null &&
      propSchema.value_write_access === 'all' &&
      hasPropertyEditor(propSchema.field_type_name)
    ) {
      properties.add(propSchema.property_type_name);
    }
  }
  if (schema.ahoy_display_name) {
    properties.add('ahoy_display_name');
  }

  const isCustomType = entityTypeName.startsWith('org_');
  const associations = new Set<string>();
  for (const name of deriveCreateAssociationNames(
    associationTypes,
    entityTypeName,
    entityKindMap
  )) {
    const assocType = associationTypes.find(
      at => at.association_type_name === name
    );
    if (!assocType) {
      continue;
    }
    // CrmCreateForm's domain-kind filter for custom types (system types keep
    // every association). Fail open while the kind map is still warming so
    // rows never flash hidden.
    if (isCustomType && entityKindMap.size > 0) {
      const keep = counterpartEntityKinds(
        assocType,
        entityTypeName,
        entityKindMap
      ).some(kind => CREATE_FORM_DOMAIN_KINDS.has(kind));
      if (!keep) {
        continue;
      }
    }
    associations.add(name);
  }
  // The stage select is core pipeline infrastructure, not a domain
  // association: GenericCrmList prepends it for pipelined custom types and
  // CrmCreateForm exempts it from the kind filter.
  for (const assocType of associationTypes) {
    if (
      assocType.source_entity_type_name === entityTypeName &&
      assocType.value_write_access === 'all' &&
      stageTypeNames.has(assocType.target_entity_type_name)
    ) {
      associations.add(assocType.association_type_name);
    }
  }

  return { properties, associations };
}
